import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { isLoggedIn, posStaffSession, savePosStaffSession } from '../../api';
import { errorText, posApi } from './api';
import './pos.css';

const VENUE_KEY = 'c2n_pos_venue_code';

// Staff sign-in on a venue tablet: the venue code (remembered on the device) and a PIN pad.
// Owners use their normal partner login instead.
const PosLogin: React.FC = () => {
  const navigate = useNavigate();
  const [venueCode, setVenueCode] = useState(() => { try { return localStorage.getItem(VENUE_KEY) ?? ''; } catch { return ''; } });
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (isLoggedIn() || posStaffSession()) return <Navigate to="/pos" replace />;

  const press = (d: string) => setPin((p) => (p.length < 6 ? p + d : p));
  const ready = !busy && pin.length >= 4 && venueCode.trim().length >= 4;
  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const r = await posApi.staffLogin(venueCode.trim(), pin);
      try { localStorage.setItem(VENUE_KEY, venueCode.trim().toUpperCase()); } catch { /* private mode */ }
      savePosStaffSession(r.accessToken, { name: r.name, role: r.role, venueName: r.venueName });
      navigate('/pos', { replace: true });
    } catch (err) {
      setError(errorText(err, 'Could not sign in.'));
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pos pos-login">
      <form className="pos-panel" onSubmit={submit}>
        <h2 className="pos-h" style={{ textAlign: 'center' }}>C2N POS</h2>
        <p className="pos-muted" style={{ textAlign: 'center', marginTop: -6 }}>Staff sign-in</p>
        {error && <div className="pos-msg err" role="alert">{error}</div>}
        <label className="pos-field">Venue code
          <input className="pos-input" value={venueCode} onChange={(e) => setVenueCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="e.g. K7PQ2M" maxLength={12} autoCapitalize="characters" autoComplete="off" />
        </label>
        {/* Type the PIN on a keyboard or tap the pad below. */}
        <label className="pos-field">PIN
          <input className="pos-input" type="password" inputMode="numeric" autoComplete="off" value={pin} maxLength={6}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} style={{ textAlign: 'center', fontSize: 24, letterSpacing: 10 }} />
        </label>
        <div className="pos-pin">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => <button key={d} type="button" onClick={() => press(d)}>{d}</button>)}
          <button type="button" onClick={() => setPin('')} aria-label="Clear">C</button>
          <button type="button" onClick={() => press('0')}>0</button>
          <button type="button" onClick={() => setPin((p) => p.slice(0, -1))} aria-label="Delete">⌫</button>
        </div>
        <button type="submit" className="pos-btn gold block" disabled={!ready}>{busy ? 'Signing in...' : 'Sign in'}</button>
        <p className="pos-muted" style={{ fontSize: 13, textAlign: 'center', marginTop: 14 }}>
          Venue owner? <Link to="/login?role=partner&returnUrl=%2Fpos" style={{ color: 'var(--pos-gold)' }}>Sign in with your partner account</Link>
        </p>
      </form>
    </div>
  );
};

export default PosLogin;
